import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { BookmarkButton } from '../components/BookmarkButton';
import { useBookmarks } from '../context/BookmarksContext';
import { Bookmark, SpinnerGap, Truck, Bus, Van, ArrowRight } from '@phosphor-icons/react';
import { OGLAttribution } from '../components/OGLAttribution';

const API = process.env.REACT_APP_BACKEND_URL;

const getSeverityClass = (s) => {
  switch (s?.toUpperCase()) {
    case 'MSI': return 'severity-serious';
    case 'VSI':
    case 'SI': return 'severity-significant';
    case 'STANDARD': return 'severity-minor';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getVehicleIcon = (type) => {
  switch (type?.toUpperCase()) {
    case 'HGV': return <Truck weight="duotone" className="h-4 w-4" />;
    case 'PSV': return <Bus weight="duotone" className="h-4 w-4" />;
    case 'LGV': return <Van weight="duotone" className="h-4 w-4" />;
    default: return <Truck weight="duotone" className="h-4 w-4" />;
  }
};

export const BookmarksPage = () => {
  const { refresh } = useBookmarks();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/bookmarks`);
      setItems(data.bookmarks || []);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    refresh();
  }, [fetchItems, refresh]);

  // Re-fetch after any toggle so list stays in sync with the star state
  const onAfterToggle = async () => {
    await fetchItems();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Bookmark weight="fill" className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Saved Offences</h1>
            <p className="text-muted-foreground text-sm">Your personal shortlist of sanctions for quick reference.</p>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <SpinnerGap weight="bold" className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-border p-12 text-center" data-testid="bookmarks-empty-state">
            <Bookmark weight="duotone" className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-semibold mb-2">No saved offences yet</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Tap the bookmark icon on any sanction to save it here for quick access.
            </p>
            <Link to="/dashboard">
              <Button className="rounded-sm" data-testid="browse-sanctions-btn">
                Browse sanctions
              </Button>
            </Link>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {items.map((s) => (
              <div key={s.id} className="flex items-start gap-3 p-4 sm:p-6 hover:bg-muted/50 transition-colors">
                <div className="pt-1">
                  <BookmarkButton sanctionId={s.id} />
                </div>
                <Link
                  to={`/sanctions/${s.id}`}
                  className="flex-1 min-w-0"
                  data-testid={`bookmark-item-${s.id}`}
                >
                  <h3 className="font-semibold text-base sm:text-lg mb-2 pr-4">{s.offence_title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{s.description}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getSeverityClass(s.severity)}`}>
                      {s.severity}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                      {getVehicleIcon(s.vehicle_type)}
                      {s.vehicle_type}
                    </span>
                    <span className="text-xs text-muted-foreground">{s.category}</span>
                  </div>
                </Link>
                <Link to={`/sanctions/${s.id}`} className="hidden sm:block pt-1">
                  <ArrowRight weight="bold" className="h-5 w-5 text-muted-foreground" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <OGLAttribution className="text-center" />
        </div>
      </footer>
    </div>
  );
};
